<template>
    <div class="site-width uk-animation-fade">
        <Seo
            :canonical="'/charity-run/' + eventId"
            :title="event ? event.name : 'Charity Run'" />

        <div class="menu-headline-distance" />

        <!-- Loading -->
        <div v-if="loading" class="uk-text-center">
            <div uk-spinner />
        </div>

        <!-- Error / closed -->
        <template v-else-if="error || !event">
            <div
                class="uk-card uk-card-alternative uk-padding site-width single uk-text-center">
                <SiteHeadline
                    switch-headline
                    is-main
                    headline="Dieses Event konnte nicht gefunden werden."
                    message="Bitte versuche es später erneut." />
            </div>
        </template>

        <template v-else-if="!event.registration_open">
            <div
                class="uk-card uk-card-alternative uk-padding site-width single uk-text-center">
                <SiteHeadline
                    switch-headline
                    is-main
                    :headline="event.name"
                    message="Die Anmeldung für dieses Event ist leider geschlossen." />
            </div>
        </template>

        <!-- Success -->
        <template v-else-if="success">
            <div
                class="uk-card uk-card-alternative uk-padding site-width single uk-text-center">
                <SiteHeadline
                    is-main
                    switch-headline
                    :message="'Danke, ' + form.forename + '!'"
                    headline="Deine Anmeldung wurde erfolgreich übermittelt. Du erhältst in Kürze eine Bestätigung per E-Mail." />
            </div>

            <div class="uk-margin-large-top uk-text-center data-struct">
                <div>
                    <span class="text-bold">Veranstaltung:</span>
                    {{ event.name }}
                </div>
                <div>
                    <span class="text-bold">Datum:</span>
                    {{ event.event_date }}
                </div>
                <div v-if="event.location">
                    <span class="text-bold">Ort:</span> {{ event.location }}
                </div>
                <div class="uk-margin-small-top">
                    <span class="text-bold">Deine Distanz:</span>
                    <span class="distance-badge"
                        >{{ form.distance_km }} km</span
                    >
                </div>
            </div>
        </template>

        <!-- Form -->
        <template v-else>
            <div class="uk-grid uk-grid-large" uk-grid>
                <!-- Left: Info -->
                <div class="uk-width-1-2@m">
                    <div class="eyebrow">{{ event.name }}</div>
                    <h1 class="hero-headline">
                        <span class="precent">Jeder Schritt</span><br />
                        <em class="accent">zählt.</em>
                    </h1>
                    <p class="hero-text" v-html="event.description"></p>

                    <div
                        class="uk-card uk-card-alternative uk-padding-small stats-card uk-margin-medium-top">
                        <div class="uk-grid" uk-grid>
                            <div v-if="false" class="uk-width-1-3">
                                <div class="stat-number">
                                    {{ event.registrations_count }}+
                                </div>
                                <div class="stat-label">Läufer Angemeldet</div>
                            </div>
                            <div class="uk-width-expand" v-if="event.location">
                                <div class="stat-number2">
                                    {{ event.event_date }}
                                </div>
                                <div class="stat-number2">
                                    {{ event.location }}
                                </div>

                                <div
                                    v-if="event.schedule"
                                    class="schedule-lines uk-margin-small-top">
                                    <div
                                        v-for="line in event.schedule.split(
                                            '\n'
                                        )"
                                        :key="line"
                                        class="schedule-line">
                                        {{ line }}
                                    </div>
                                </div>
                            </div>
                            <div class="uk-width-small uk-text-right">
                                <img
                                    style="max-height: 130px"
                                    src="/assets/runner.svg"
                                    uk-img />
                            </div>
                        </div>
                    </div>

                    <!-- Supporter logo -->
                    <div
                        v-if="event.supporter_logo"
                        class="supporter-wrap uk-margin-medium-top">
                        <div class="supporter-label">In Zusammenarbeit mit</div>
                        <img
                            :src="event.supporter_logo"
                            alt="Supporter"
                            class="supporter-logo" />
                    </div>
                </div>

                <!-- Right: Form -->
                <div class="uk-width-1-2@m">
                    <div class="uk-card uk-card-alternative uk-padding">
                        <h2 class="form-title uk-margin-bottom">
                            Mitmachen &amp; Gutes Tun
                        </h2>

                        <span id="error-box" />
                        <InfoBox
                            mode="danger"
                            slim
                            medium
                            v-if="formError"
                            class="uk-margin-small-bottom uk-animation-fade uk-margin-medium-bottom">
                            {{ formError }}
                        </InfoBox>

                        <!-- One field per line: side-by-side inputs left the
                             date field too narrow to tap on a phone. -->
                        <div class="uk-margin-small-top">
                            <label class="field-label">Vorname</label>
                            <input
                                v-model="form.forename"
                                name="name"
                                class="uk-input field-input"
                                type="text" />
                        </div>

                        <div class="uk-margin-top">
                            <label class="field-label">Name</label>
                            <input
                                name="name"
                                v-model="form.surname"
                                class="uk-input field-input"
                                type="text" />
                        </div>

                        <div class="uk-margin-top">
                            <label class="field-label">E-Mail Adresse</label>
                            <input
                                v-model="form.email"
                                class="uk-input field-input"
                                :class="{'input-error': emailInvalid}"
                                type="email"
                                @blur="validateEmail" />
                            <div v-if="emailInvalid" class="field-error">
                                Bitte eine gültige E-Mail-Adresse eingeben.
                            </div>
                        </div>

                        <div class="uk-margin-top">
                            <label class="field-label">Geburtsdatum</label>
                            <input
                                v-model="form.birthdate"
                                class="uk-input field-input"
                                type="date"
                                :min="minBirthdate"
                                :max="maxBirthdate" />
                        </div>

                        <!-- Distance selector — from API -->
                        <div class="uk-margin-top">
                            <label class="field-label"
                                >Wähle deine Distanz</label
                            >
                            <div
                                class="uk-grid uk-grid-small"
                                :class="
                                    'uk-child-width-1-' + event.distances.length
                                "
                                uk-grid>
                                <div
                                    v-for="d in event.distances"
                                    :key="d.value">
                                    <div
                                        class="distance-card"
                                        :class="{
                                            active:
                                                form.distance_km === d.value,
                                        }"
                                        @click="form.distance_km = d.value">
                                        <div class="distance-km">
                                            {{ formatDistance(d.value) }} km
                                        </div>
                                        <div class="distance-label">
                                            {{ d.label }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Team (optional) -->
                        <div class="uk-margin-top">
                            <label class="field-label"
                                >Verein / Unternehmen (optional)</label
                            >
                            <input
                                v-model="form.team_name"
                                class="uk-input field-input"
                                type="text" />
                        </div>

                        <!-- Submit -->
                        <div class="uk-margin-medium-top uk-text-center">
                            <Button
                                is-large
                                has-action
                                :action="submit"
                                :disabled="submitting"
                                mode="success"
                                class="uk-width-1-1">
                                {{
                                    submitting
                                        ? 'Wird gesendet...'
                                        : 'Jetzt Registrieren →'
                                }}
                            </Button>
                        </div>

                        <div class="uk-margin-top uk-text-center consent-text">
                            Mit der Anmeldung akzeptierst du unsere
                            Teilnahmebedingungen und die Datenschutzerklärung.
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div class="uk-margin-large-top" />
    </div>
</template>

<script>
import Seo from '../../components/Seo.vue';
import SiteHeadline from '@/components/SiteHeadline.vue';
import Button from '../../components/Button.vue';
import axios from 'axios';
import InfoBox from '@/components/InfoBox.vue';
import UIkit from 'uikit';

export default {
    name: 'CharityRun',
    components: {InfoBox, Seo, SiteHeadline, Button},
    data() {
        return {
            loading: true,
            error: null,
            event: null,
            success: false,
            submitting: false,
            formError: null,
            emailInvalid: false,
            form: {
                forename: '',
                surname: '',
                email: '',
                birthdate: '',
                distance_km: null,
                address_line1: '',
                team_name: '',
            },
        };
    },
    computed: {
        eventId() {
            return this.$route.params.eventId;
        },
        // Bounds for the native date input – both as YYYY-MM-DD.
        maxBirthdate() {
            const d = new Date();
            d.setFullYear(d.getFullYear() - 5);
            return this.toIsoDate(d);
        },
        minBirthdate() {
            return '1900-01-01';
        },
    },
    created() {
        this.loadEvent();
    },
    methods: {
        // Local calendar date as YYYY-MM-DD. Never use toISOString() here – it
        // converts to UTC and shifts the date a day back in German timezones.
        toIsoDate(date) {
            const pad = (n) => String(n).padStart(2, '0');
            return (
                date.getFullYear() +
                '-' +
                pad(date.getMonth() + 1) +
                '-' +
                pad(date.getDate())
            );
        },
        formatDistance(val) {
            return String(val).replace('.', ',');
        },
        // The API answers with either {message: '…'} or a 422 field->errors map.
        // Showing that beats a generic "something went wrong".
        extractError(e) {
            const fallback =
                'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
            const data = e && e.response && e.response.data;
            if (!data) return fallback;
            if (typeof data.message === 'string') return data.message;

            const messages = Object.keys(data)
                .map((key) =>
                    Array.isArray(data[key]) ? data[key][0] : data[key]
                )
                .filter((m) => typeof m === 'string');

            return messages.length ? messages.join(' ') : fallback;
        },
        validateEmail() {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            this.emailInvalid =
                this.form.email.length > 0 && !re.test(this.form.email);
        },
        loadEvent() {
            this.loading = true;
            axios
                .get(
                    this.getBaseUrl + '/api/charity-runs/events/' + this.eventId
                )
                .then((res) => {
                    this.event = res.data;
                    // Pre-select first distance from API
                    if (res.data.distances && res.data.distances.length > 0) {
                        this.form.distance_km = res.data.distances[0].value;
                    }
                    this.loading = false;
                })
                .catch((e) => {
                    this.error = e;
                    this.loading = false;
                });
        },
        submit() {
            this.formError = null;

            if (
                !this.form.forename ||
                !this.form.surname ||
                !this.form.email ||
                !this.form.birthdate
            ) {
                this.formError = 'Bitte fülle alle Pflichtfelder aus.';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo(
                    '#error-box'
                );
                return;
            }

            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!re.test(this.form.email)) {
                this.emailInvalid = true;
                this.formError = 'Bitte eine gültige E-Mail-Adresse eingeben.';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo(
                    '#error-box'
                );
                return;
            }

            if (
                this.form.birthdate < this.minBirthdate ||
                this.form.birthdate > this.maxBirthdate
            ) {
                this.formError =
                    'Bitte ein gültiges Geburtsdatum eingeben (Mindestalter 5 Jahre).';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo(
                    '#error-box'
                );
                return;
            }

            if (!this.form.distance_km) {
                this.formError = 'Bitte wähle deine Distanz.';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo(
                    '#error-box'
                );
                return;
            }

            this.submitting = true;

            axios
                .post(this.getBaseUrl + '/api/charity-runs/register', {
                    event_id: this.event.id,
                    forename: this.form.forename,
                    surname: this.form.surname,
                    email: this.form.email,
                    birthdate: this.form.birthdate,
                    distance_km: this.form.distance_km,
                    address_line1: this.form.address_line1 || undefined,
                    team_name: this.form.team_name || undefined,
                })
                .then(() => {
                    this.success = true;
                    window.scrollTo({top: 0, behavior: 'smooth'});
                })
                .catch((e) => {
                    this.formError = this.extractError(e);
                    UIkit.scroll('#error-box', {offset: 100}).scrollTo(
                        '#error-box'
                    );
                    console.error(e);
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.schedule-lines {
    font-weight: 500;
    font-size: 18px;
}

.eyebrow {
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #3ddc97;
    margin-bottom: 12px;
}

.hero-headline {
    font-size: 3rem;
    font-weight: 700;
    line-height: 1.1;
    margin: 0 0 20px;
}

.accent {
    color: #3ddc97;
    font-style: italic;
}
.precent {
    color: #fff !important;
}

.hero-text {
    font-size: 18px;
    line-height: 1.6;
    opacity: 0.85;
}

.stats-card {
    border-radius: 8px;
}

.stat-number {
    font-size: 1.7rem;
    font-weight: 700;
}

.stat-number2 {
    font-size: 20px;
    font-weight: 600;
}

.stat-address {
    font-size: 13px;
    opacity: 0.6;
    margin-top: 2px;
}

.stat-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    opacity: 0.6;
    margin-top: 2px;
}

.supporter-wrap {
}

.supporter-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.5;
    margin-bottom: 8px;
}

.supporter-logo {
    max-height: 48px;
    max-width: 180px;
    object-fit: contain;
}

.form-title {
    font-size: 1.6rem;
    font-weight: 600;
    margin: 0 0 16px;
    color: #ddd;
}

.field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.7;
    margin-bottom: 6px;
}

.field-input {
    background: #1e1e26 !important;
    border: 1px solid #333 !important;
    color: #fff !important;
    border-radius: 6px;
    height: 48px;

    &::placeholder {
        opacity: 0.35;
    }
    &:focus {
        border-color: #3ddc97 !important;
        outline: none;
    }

    // Native date input: keep the OS picker/icon legible on the dark card and
    // stop iOS Safari from collapsing the field to its intrinsic width.
    &[type='date'] {
        color-scheme: dark;
        display: block;
        width: 100%;
        min-width: 0;
        -webkit-appearance: none;
        appearance: none;

        &::-webkit-date-and-time-value {
            text-align: left;
            // iOS Safari lays the value out at the top of the box instead of
            // on the centre line, so the date sat above the other fields'
            // text. Giving it the field's inner height as line-height is what
            // pulls it back into the middle: 48px minus the two 1px borders.
            // Desktop centres it anyway, so this is a no-op there.
            line-height: 46px;
        }
        // No invert() here: `color-scheme: dark` above already makes the
        // browser draw the indicator as a light glyph, and inverting that
        // turns it black again.
        &::-webkit-calendar-picker-indicator {
            opacity: 0.6;
            cursor: pointer;
        }
    }
}

.input-error {
    border-color: #f44336 !important;
}
.field-error {
    font-size: 12px;
    color: #f44336;
    margin-top: 4px;
}

.distance-card {
    background: #1e1e26;
    border: 1px solid #333;
    border-radius: 8px;
    padding: 16px 10px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;

    &:hover {
        border-color: #555;
    }

    &.active {
        background: #3ddc97;
        border-color: #3ddc97;
        .distance-km,
        .distance-label {
            color: #121217;
        }
    }
}

.distance-km {
    font-size: 1.3rem;
    font-weight: 700;
    font-style: italic;
}
.distance-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-top: 4px;
    opacity: 0.7;
}

.distance-badge {
    display: inline-block;
    background: #3ddc97;
    color: #121217;
    border-radius: 6px;
    padding: 2px 12px;
    font-weight: 700;
    margin-left: 6px;
}

.data-struct {
    font-size: 20px;
}
.text-bold {
    font-weight: 500;
}

.consent-text {
    font-size: 13px;
    opacity: 0.5;
}

.uk-alert-danger {
    background-color: transparent !important;
}
</style>
